import fp from 'lodash/fp';
import { call, takeLatest, put, select } from 'redux-saga/effects';
import {
    selectDonationCurrency,
    updateDonationAmount,
    updateDonationAmountWithCart,
    updateDonationCurrency,
    updateGivingPaymentOptions,
    updateGivingAmount,
} from '../checkout/checkoutSlice';
import {
    addPerkLineItem,
    selectPerkLineItems,
    updateAmountLineItem,
    updatePerkLineItemAmounts,
    replaceCart,
    clearCart,
    addPerk,
    triggerValidation,
    setValidationErrors,
    selectPerkAmountProperty,
    showCartValidationErrors,
    selectCartTotal,
    selectPerkLineItemTotal,
    addNewPerk,
    clearAmountLineItems,
    selectCartHasUnspecifiedAmountPerk,
} from './cartSlice';
import { convertDonationAmount } from 'common/helpers/convertAmount';
import {
    selectCurrencySettings,
    selectPageName,
    selectProductSettings,
    selectSelectedCampaign,
} from '../campaign/campaignSlice';
import { resetCheckoutPopup } from '../checkout/checkoutPopupSlice';
import { showNotification } from '../notifications/notificationsSlice';
import { createPageOptionsSelector } from 'client/components/GivingOptionsEditor/givingSelectors';
import { validateCartPerkLineItems } from 'client/helpers/cart';

function* updateDonationAmountSaga({ payload: donationAmount }) {
    const productSettings = yield select(selectProductSettings);
    const cartHasUnspecifiedAmountPerk = yield select(
        selectCartHasUnspecifiedAmountPerk,
    );
    const perkLineItems = yield select(selectPerkLineItems);
    // when shopping cart is disabled, and unspecified amount perk is selected
    // amount field is used to specify perk amount
    if (
        !productSettings?.isShoppingCartEnabled &&
        cartHasUnspecifiedAmountPerk &&
        perkLineItems.length === 1
    ) {
        const perkLineItem = perkLineItems[0];
        return yield put(
            updatePerkLineItemAmounts([
                {
                    perkId: perkLineItem.perkId,
                    amount: donationAmount,
                    recurringAmount: donationAmount,
                },
            ]),
        );
    }
    if (productSettings?.enableAdditionalAmount) {
        yield put(updateAmountLineItem(donationAmount));
    } else {
        yield call(replaceCartSaga, { payload: { amount: donationAmount } });
    }
}

function* clearAmountLineItemSaga() {
    const productSettings = yield select(selectProductSettings);
    if (!productSettings?.enableAdditionalAmount) {
        yield put(clearAmountLineItems());
        yield call(calculatePerkLineItemAmountsSaga);
    }
}

function* calculatePerkLineItemAmountsSaga() {
    const donationCurrency = yield select(selectDonationCurrency);
    const currencySettings = yield select(selectCurrencySettings);
    const { primaryCurrency, currencyRates } = currencySettings;
    const perkLineItems = yield select(selectPerkLineItems);

    const perkLineItemAmountUpdates = perkLineItems
        .map(li => {
            const amountUpdates = {};
            if (li.primaryCurrencyAmount) {
                amountUpdates.amount = convertDonationAmount(
                    currencyRates,
                    primaryCurrency,
                    donationCurrency,
                    li.primaryCurrencyAmount,
                );
            }
            if (li.primaryCurrencyRecurringAmount) {
                amountUpdates.recurringAmount = convertDonationAmount(
                    currencyRates,
                    primaryCurrency,
                    donationCurrency,
                    li.primaryCurrencyRecurringAmount,
                );
            }
            if (fp.isEmpty(amountUpdates)) {
                return null;
            }
            return { perkId: li.perkId, ...amountUpdates };
        })
        .filter(Boolean);

    yield put(updatePerkLineItemAmounts(perkLineItemAmountUpdates));

    const perkLineItemsTotal = yield select(selectPerkLineItemTotal);
    const totalAmount = yield select(selectCartTotal);
    yield put(updateGivingAmount(perkLineItemsTotal));
    yield put(updateDonationAmountWithCart(totalAmount));
}

// needed for cases where shopping cart feature is not enabled
function* replaceCartSaga({ payload: { perk, amount } }) {
    yield put(clearCart());
    if (perk) {
        yield put(addPerkLineItem(perk));
    }
    const parsedAmount = parseInt(amount, 10);
    if (!isNaN(parsedAmount)) {
        yield put(updateAmountLineItem(parsedAmount));
    }
}

function* validateCartSaga() {
    const totalAmount = yield select(selectCartTotal);
    const perkLineItems = yield select(selectPerkLineItems);
    const perkAmountProperty = yield select(selectPerkAmountProperty);
    const currency = yield select(selectDonationCurrency);

    const validationErrors = validateCartPerkLineItems({
        currency,
        totalAmount,
        perkLineItems,
        perkAmountProperty,
    });

    yield put(setValidationErrors(validationErrors));
}

function* showValidationErrorsSaga() {
    yield put(showCartValidationErrors(true));
}

function* addPerkNotificationSaga({ payload: perkData }) {
    const selectedPage = yield select(selectPageName);
    const campaign = yield select(selectSelectedCampaign);
    const defaultPage = campaign?.defaultPage;
    const getPerkPageOption = createPageOptionsSelector(
        selectedPage,
        defaultPage,
    );
    const perkLineItems = yield select(selectPerkLineItems);
    const isAlreadyAdded = perkLineItems.some(
        li => li.perkId === perkData.perkId && li.quantity > 1,
    );
    if (isAlreadyAdded) {
        yield put(
            showNotification({
                messageId: 'shoppingCart.addAnotherItem',
                values: { itemName: getPerkPageOption(perkData, 'name') },
            }),
        );
    } else {
        yield put(
            showNotification({
                messageId: 'shoppingCart.itemAdded',
                values: { itemName: getPerkPageOption(perkData, 'name') },
            }),
        );
    }
}

export default function* cartSaga() {
    yield takeLatest(replaceCart.type, replaceCartSaga);
    yield takeLatest(updateDonationAmount.type, updateDonationAmountSaga);
    yield takeLatest(addPerk.type, addPerkNotificationSaga);
    yield takeLatest(addNewPerk.type, addPerkNotificationSaga);
    yield takeLatest(addPerk.type, clearAmountLineItemSaga); // when additional amount is disabled
    yield takeLatest(addNewPerk.type, clearAmountLineItemSaga); // when additional amount is disabled
    yield takeLatest(addPerkLineItem.type, calculatePerkLineItemAmountsSaga);
    yield takeLatest(addPerk.type, calculatePerkLineItemAmountsSaga);
    yield takeLatest(addNewPerk.type, calculatePerkLineItemAmountsSaga);
    yield takeLatest(
        updateDonationCurrency.type,
        calculatePerkLineItemAmountsSaga,
    );
    yield takeLatest(resetCheckoutPopup.type, calculatePerkLineItemAmountsSaga);
    yield takeLatest(
        updateGivingPaymentOptions.type,
        calculatePerkLineItemAmountsSaga,
    );
    yield takeLatest(triggerValidation.type, validateCartSaga);
    yield takeLatest(triggerValidation.type, showValidationErrorsSaga);
    yield takeLatest(updatePerkLineItemAmounts.type, validateCartSaga);
    yield takeLatest(updateAmountLineItem.type, validateCartSaga);
}
